import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { OrganizationInvitationService } from '../../core/data-services/organization-invitation.service';
import { MembershipRoles } from '../../core/data-models/membership-roles.model';

/**
 * Admin Invite Member Modal Component
 *
 * Modal for inviting members to an organization from the admin portal.
 * Supports inviting new users or existing users.
 */
@Component({
  selector: 'app-admin-invite-member-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  template: `
    <div class="modal-backdrop fade show" (click)="onClose()"></div>
    <div class="modal fade show d-block" tabindex="-1">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Invite Member to {{ organizationName }}</h5>
            <button type="button" class="btn-close" (click)="onClose()"></button>
          </div>
          <form [formGroup]="inviteForm" (ngSubmit)="onSubmit()">
            <div class="modal-body">
              <!-- Error Message -->
              <div class="alert alert-danger" *ngIf="errorMessage">
                {{ errorMessage }}
              </div>

              <!-- Email -->
              <div class="mb-3">
                <label for="email" class="form-label">Email <span class="text-danger">*</span></label>
                <input
                  type="email"
                  class="form-control"
                  id="email"
                  formControlName="email"
                  placeholder="pilot@example.com"
                  [class.is-invalid]="inviteForm.get('email')?.invalid && inviteForm.get('email')?.touched"
                />
                <div class="invalid-feedback" *ngIf="inviteForm.get('email')?.invalid && inviteForm.get('email')?.touched">
                  Please enter a valid email address
                </div>
              </div>

              <!-- First Name -->
              <div class="mb-3">
                <label for="firstName" class="form-label">First Name</label>
                <input
                  type="text"
                  class="form-control"
                  id="firstName"
                  formControlName="firstName"
                  placeholder="John"
                />
              </div>

              <!-- Last Name -->
              <div class="mb-3">
                <label for="lastName" class="form-label">Last Name</label>
                <input
                  type="text"
                  class="form-control"
                  id="lastName"
                  formControlName="lastName"
                  placeholder="Doe"
                />
              </div>

              <!-- Roles -->
              <div class="mb-3">
                <label class="form-label">Assign Roles <span class="text-danger">*</span></label>
                <div class="form-check">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    id="rolePilot"
                    [checked]="hasRole(MembershipRoles.Pilot)"
                    (change)="toggleRole(MembershipRoles.Pilot)"
                  />
                  <label class="form-check-label" for="rolePilot">
                    Pilot
                  </label>
                </div>
                <div class="form-check">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    id="rolePIC"
                    [checked]="hasRole(MembershipRoles.PilotInCharge)"
                    (change)="toggleRole(MembershipRoles.PilotInCharge)"
                  />
                  <label class="form-check-label" for="rolePIC">
                    Pilot in Command
                  </label>
                </div>
              </div>

              <div class="alert alert-info">
                <i class="bi bi-info-circle"></i>
                An invitation email will be sent to the member with instructions to join.
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" (click)="onClose()" [disabled]="submitting">
                Cancel
              </button>
              <button type="submit" class="btn btn-primary" [disabled]="inviteForm.invalid || submitting">
                <span *ngIf="submitting">
                  <span class="spinner-border spinner-border-sm me-1"></span>
                  Sending...
                </span>
                <span *ngIf="!submitting">Send Invitation</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
    <div class="modal-backdrop fade show"></div>
  `,
})
export class AdminInviteMemberModalComponent {
  @Input() organizationId!: string;
  @Input() organizationName!: string;
  @Output() closed = new EventEmitter<void>();
  @Output() invited = new EventEmitter<void>();

  inviteForm: FormGroup;
  submitting = false;
  errorMessage = '';
  selectedRole = MembershipRoles.Pilot; // Default role

  // Expose enum for template
  MembershipRoles = MembershipRoles;

  constructor(
    private fb: FormBuilder,
    private invitationService: OrganizationInvitationService
  ) {
    this.inviteForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      firstName: [''],
      lastName: [''],
    });
  }

  /**
   * Check if role is selected
   */
  hasRole(role: MembershipRoles): boolean {
    return (this.selectedRole & role) === role;
  }

  /**
   * Toggle role selection
   */
  toggleRole(role: MembershipRoles): void {
    if (this.hasRole(role)) {
      this.selectedRole &= ~role; // Remove role
    } else {
      this.selectedRole |= role; // Add role
    }
  }

  /**
   * Submit form
   */
  async onSubmit(): Promise<void> {
    if (this.inviteForm.invalid || !this.selectedRole) {
      return;
    }

    this.submitting = true;
    this.errorMessage = '';

    try {
      const formValue = this.inviteForm.value;
      await this.invitationService.inviteMember(this.organizationId, {
        email: formValue.email,
        firstName: formValue.firstName,
        lastName: formValue.lastName,
        role: this.selectedRole,
      });

      this.invited.emit();
    } catch (error: any) {
      console.error('Failed to invite member:', error);
      this.errorMessage = error.message || 'Failed to invite member. Please try again.';
    } finally {
      this.submitting = false;
    }
  }

  /**
   * Close modal
   */
  onClose(): void {
    this.closed.emit();
  }
}
